// src/pages/GalleryPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { galleryItemService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import useApi from '../hooks/useApi';

// Styled components avec styles simplifiés pour éviter les erreurs Emotion
const GallerySection = styled.section`
  padding: 5rem 2rem;
  background: ${props => props.theme.backgroundGradient || `linear-gradient(to bottom, ${props.theme.background}, ${props.theme.backgroundSecondary})`};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const GalleryGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  grid-auto-flow: dense;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  box-shadow: ${props => props.theme.card.shadow};
  cursor: pointer;
  background: ${props => props.theme.backgroundSecondary};
  display: flex;
  flex-direction: column;
  
  &:hover .hover-icon {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  
  &:hover .item-image {
    opacity: 0.8;
  }
  
  &.wide {
    grid-column: span 2;
  }
  
  &.tall {
    grid-row: span 2;
  }
  
  &.featured {
    grid-column: span 2;
    grid-row: span 2;
  }
  
  @media (max-width: 768px) {
    &.wide, &.tall, &.featured {
      grid-column: span 1;
      grid-row: span 1;
    }
  }
`;

const ItemImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  
  .gallery-item.featured & {
    height: 300px;
  }
  
  .gallery-item.wide & {
    height: 220px;
  }
  
  .gallery-item.tall & {
    height: 350px;
  }
`;

const ItemImage = styled(motion.div)`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  transition: all 0.3s ease-in-out;
`;

const HoverIcon = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  background: ${props => props.theme.primary};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.5rem;
  z-index: 5;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  opacity: 0;
  transition: all 0.3s ease;
`;

const ItemInfo = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ItemTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ItemDescription = styled.p`
  color: ${props => props.theme.textLight || props.theme.text + '90'};
  font-size: 0.9rem;
  margin-bottom: 0.8rem;
  line-height: 1.4;
  flex: 1;
  
  /* Limiter à 3 lignes */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryTag = styled.div`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  background: ${props => props.theme.primary}30;
  color: ${props => props.theme.primary};
  font-size: 0.8rem;
  font-weight: 500;
  align-self: flex-start;
`;

const VideoIcon = styled(motion.div)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.primary};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1rem;
  z-index: 5;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
`;

const LightboxOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const LightboxContent = styled(motion.div)`
  position: relative;
  max-width: 90%;
  max-height: 80vh;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  
  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
  }
  
  iframe {
    width: 90vw;
    height: 50.625vw; /* 16:9 aspect ratio */
    max-height: 75vh;
    border: none;
  }
`;

const LightboxClose = styled(motion.button)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 1.8rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const LightboxNav = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 1001;
  
  &.prev {
    left: 2rem;
  }
  
  &.next {
    right: 2rem;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }
`;

const LightboxCaption = styled(motion.div)`
  color: white;
  text-align: center;
  padding: 1.5rem;
  max-width: 800px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  margin-top: 1.5rem;
  backdrop-filter: blur(10px);
  
  h3 {
    font-size: 1.8rem;
    margin-bottom: 0.8rem;
    font-weight: 600;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const LoadingIndicator = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 5rem 2rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
  background: ${props => props.theme.backgroundSecondary}40;
  border-radius: 16px;
  backdrop-filter: blur(5px);
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 12 }
  },
  hover: {
    y: -5,
    boxShadow: "0 15px 30px rgba(0,0,0,0.2)",
    transition: { type: "spring", stiffness: 300, damping: 10 }
  },
  tap: { scale: 0.98 }
};

const GalleryPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [currentItem, setCurrentItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const controls = useAnimation();
  
  // Référence pour l'animation avec IntersectionObserver
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  // Animation lors du scroll dans la vue
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);
  
  // Charger les éléments de galerie
  const { 
    data: galleryItems, 
    loading: itemsLoading, 
    error: itemsError 
  } = useApi(() => galleryItemService.getAll(), []);
  
  // Préparer les éléments avec leur mise en page
  const displayItems = React.useMemo(() => {
    // Vérifier si galleryItems est un tableau valide
    if (!galleryItems || !Array.isArray(galleryItems)) return [];
    
    return galleryItems.map((item, index) => {
      // Calculer dynamiquement la mise en page
      let layoutClass = '';
      
      if (item.is_featured) {
        layoutClass = 'featured';
      } else {
        layoutClass = index % 5 === 0 ? 'wide' : index % 7 === 1 ? 'tall' : '';
      }
      
      return { ...item, layoutClass };
    });
  }, [galleryItems]);
  
  // Ouvrir la lightbox sur un élément
  const openLightbox = (item) => {
    setCurrentItem(item);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Empêcher le défilement du corps
  };
  
  // Fermer la lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto'; // Réactiver le défilement du corps
  };
  
  // Navigation dans la lightbox
  const navigateLightbox = (direction) => {
    if (!displayItems.length) return;
    
    const currentIndex = displayItems.findIndex(item => item.id === currentItem.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex === displayItems.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? displayItems.length - 1 : currentIndex - 1;
    }
    
    setCurrentItem(displayItems[newIndex]);
  };
  
  // Gestion des touches de clavier pour la navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentItem]);

  // Animation au chargement de la page
  useEffect(() => {
    controls.start("visible");
  }, [controls]);
  
  return (
    <GallerySection theme={theme}>
      <Container>
        <SectionTitle
          title={t('gallery')}
          subtitle={t('gallerySubtitle')}
          highlight
          animated
        />
        
        {itemsLoading ? (
          <LoadingIndicator
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{ 
                rotate: 360,
                transition: { 
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity
                }
              }}
            >
              <svg width="60" height="60" viewBox="0 0 50 50">
                <circle 
                  cx="25" 
                  cy="25" 
                  r="20" 
                  fill="none" 
                  stroke={theme.primary} 
                  strokeWidth="4" 
                  strokeLinecap="round"
                >
                  <animate 
                    attributeName="stroke-dasharray" 
                    from="1, 150" 
                    to="90, 150" 
                    dur="1.5s" 
                    repeatCount="indefinite"
                  />
                  <animate 
                    attributeName="stroke-dashoffset" 
                    from="0" 
                    to="-35" 
                    dur="1.5s" 
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </motion.div>
          </LoadingIndicator>
        ) : itemsError ? (
          <motion.div 
            style={{ textAlign: 'center', padding: '3rem 0' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>
              {itemsError}
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '0.75rem 2rem',
                background: theme.primary,
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: `0 6px 15px ${theme.primary}40`
              }}
              onClick={() => window.location.reload()}
            >
              <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
              {t('tryAgain')}
            </motion.button>
          </motion.div>
        ) : displayItems.length === 0 ? (
          <NoResults 
            theme={theme}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
              <i className="fas fa-images"></i>
            </div>
            <h3>{t('noItemsFound')}</h3>
            <p>{t('noGalleryItems')}</p>
          </NoResults>
        ) : (
          <GalleryGrid 
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={controls}
          >
            {displayItems.map((item, index) => (
              <GalleryItem
                key={item.id}
                theme={theme}
                className={`gallery-item ${item.layoutClass}`}
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => openLightbox(item)}
                layoutId={`gallery-item-${item.id}`}
                custom={index}
              >
                <ItemImageContainer>
                  <ItemImage 
                    className="item-image"
                    src={item.image} 
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.8 }}
                  />
                  
                  <HoverIcon 
                    className="hover-icon"
                    theme={theme}
                  >
                    <i className={item.item_type === 'video' ? 'fas fa-play' : 'fas fa-link'}></i>
                  </HoverIcon>
                  
                  {item.item_type === 'video' && (
                    <VideoIcon 
                      theme={theme}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <i className="fas fa-play"></i>
                    </VideoIcon>
                  )}
                </ItemImageContainer>
                
                <ItemInfo theme={theme}>
                  <ItemTitle theme={theme}>{item.title}</ItemTitle>
                  {item.description && (
                    <ItemDescription theme={theme}>
                      {item.description}
                    </ItemDescription>
                  )}
                  {item.category && (
                    <CategoryTag theme={theme}>
                      {item.category.name}
                    </CategoryTag>
                  )}
                </ItemInfo>
              </GalleryItem>
            ))}
          </GalleryGrid>
        )}
        
        {/* Lightbox modal with enhanced animations */}
        <AnimatePresence>
          {lightboxOpen && currentItem && (
            <LightboxOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeLightbox}
            >
              <LightboxClose 
                onClick={closeLightbox}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fas fa-times"></i>
              </LightboxClose>
              
              <LightboxNav 
                className="prev" 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                whileHover={{ scale: 1.1, x: -5 }}
                whileTap={{ scale: 0.9 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <i className="fas fa-chevron-left"></i>
              </LightboxNav>
              
              <LightboxContent 
                onClick={(e) => e.stopPropagation()}
                layoutId={`gallery-item-${currentItem.id}`}
              >
                {currentItem.item_type === 'video' ? (
                  <motion.iframe 
                    src={currentItem.video_url} 
                    title={currentItem.title}
                    allowFullScreen
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  ></motion.iframe>
                ) : (
                  <motion.img 
                    src={currentItem.image} 
                    alt={currentItem.title}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </LightboxContent>
              
              <LightboxNav 
                className="next" 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                whileHover={{ scale: 1.1, x: 5 }}
                whileTap={{ scale: 0.9 }}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <i className="fas fa-chevron-right"></i>
              </LightboxNav>
              
              <LightboxCaption
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3>{currentItem.title}</h3>
                {currentItem.description && <p>{currentItem.description}</p>}
                {currentItem.category && (
                  <motion.div 
                    style={{ 
                      marginTop: '1rem',
                      display: 'inline-block',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '20px',
                      background: `${theme.primary}80`,
                      color: 'white',
                      fontSize: '0.9rem'
                    }}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {currentItem.category.name}
                  </motion.div>
                )}
              </LightboxCaption>
            </LightboxOverlay>
          )}
        </AnimatePresence>
      </Container>
    </GallerySection>
  );
};

export default GalleryPage;